import { yupResolver } from '@hookform/resolvers';
import FloatButton from '@ifca-root/react-component/src/components/Button/FloatButton';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import DynamicSubHeader from '@ifca-root/react-component/src/components/Header/DynamicSubHeader';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { smallTitle } from '@ifca-root/react-component/src/helpers/Global/ContractGlobalVar';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { ErrorDialog } from 'components/Dialog/ErrorDialog';
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog';
import AppContext from 'containers/App/Store/AppContext';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import {
  GetLetterTemplateDocument,
  useCreateLetterTemplateMutation,
  useDeleteLetterTemplateMutation,
  useGetLetterTemplateQuery,
  useGetStandardLetterQuery,
  useUpdateLetterTemplateMutation,
} from 'generated/graphql';
import { handleExitConfirmation } from 'helpers/Form/ExitConfirmation';
import { useMenuOption } from 'helpers/Hooks/useMenuOption';
import { SystemMsgs } from 'helpers/Messages/SystemMsg';
import { CommonYupValidation } from 'helpers/YupSchema/yup';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router';
import * as yup from 'yup';

export interface LetterTemplateProps {
  name: string;
  jointBuyerSignRequired: boolean;
  isReportDesignEditor: boolean;
  isDefault: boolean;
  isWatermark: boolean;
  watermarkText?: string;
}

export const LetterTemplateListing = (props: any) => {
  const { pageMode, standardLetterID }: any = useParams();

  let history = useHistory();
  let location: any = useLocation();
  const {
    anchorEl,
    menu,
    handleClick,
    handleClose,
    resetMenu,
  } = useMenuOption();
  const { globalState, dispatch }: any = useContext(AppContext as any);
  const user = JSON.parse(localStorage.getItem('loggedInUser'));
  const isHomeDefault = localStorage.getItem('isDefault');
  const [deleteLT, setDeleteLT] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openExitConf, setOpenExitConf] = useState<boolean>(false);
  const [formMode, setFormMode] = useState('');
  const [errMessage, setErrMessage] = useState(null);
  const [errDialog, setErrDialog] = useState(false);
  const [isWatermark, setIsWatermark] = useState(false);

  const LetterTemplateFormSchema = yup.object().shape({
    name: CommonYupValidation.requireField(SystemMsgs.name()),
    ...(!!isWatermark && {
      watermarkText: CommonYupValidation.requireField(SystemMsgs.watermark()),
    }),
  });
  const pathParts = location.pathname.split('/');
  const mode = pathParts.includes('general-settings')
    ? 'general-settings'
    : pathParts.includes('company-setup')
    ? 'company-setup'
    : '';

  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
    SnackBarContext as any,
  );

  const {
    register,
    handleSubmit,
    errors,
    clearErrors,
    control,
    setValue,
    setError,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<LetterTemplateProps>({
    defaultValues: {
      isWatermark: menu?.obj?.isWatermark ?? false,
    },
    shouldUnregister: false, // unmount input state will be remained
    resolver: yupResolver(LetterTemplateFormSchema),
  });

  const {
    loading: GetStandardLetterloading,
    data: { getStandardLetter } = { getStandardLetter: [] },
  } = useGetStandardLetterQuery({
    fetchPolicy: 'network-only',
    variables: { standardLetterID },
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  const {
    loading: GetLetterTemplateLoading,
    data: { getLetterTemplate } = { getLetterTemplate: [] },
  } = useGetLetterTemplateQuery({
    fetchPolicy: 'network-only',
    variables: { standardLetterID },
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  const [
    createLetterTemplate,
    { loading: CreateLetterTemplateLoading },
  ] = useCreateLetterTemplateMutation({
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
    onCompleted: ({ createLetterTemplate }) => {
      setOpenSnackBar(true);
      setSnackBarMsg(SystemMsgs.createNewRecord());
      if (createLetterTemplate) {
        setSnackBarMsg('Created Successfully!');
        setOpen(false);
      } else {
        setSnackBarMsg('Create Failed!');
      }
    },
  });

  const [
    updateLetterTemplate,
    { loading: UpdateLetterTemplateLoading },
  ] = useUpdateLetterTemplateMutation({
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
    onCompleted: ({ updateLetterTemplate }) => {
      if (updateLetterTemplate) {
        setOpenSnackBar(true);
        setSnackBarMsg(SystemMsgs.updateRecord());
        setOpen(false);
      } else {
        setSnackBarMsg('Update Failed!');
      }
    },
  });

  const [
    deleteLetterTemplate,
    { loading: DeleteLetterTemplateLoading },
  ] = useDeleteLetterTemplateMutation({
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
    onCompleted: ({ deleteLetterTemplate }) => {
      setOpenSnackBar(true);
      setSnackBarMsg(SystemMsgs.deleteRecord());
      setDeleteLT(false);
      if (deleteLetterTemplate) {
        setSnackBarMsg('Deleted Successfully!');
        setDeleteLT(false);
      } else {
        setSnackBarMsg('Delete Failed!');
      }
    },
  });

  let headerTitle: string = '';
  switch (pageMode) {
    case 'client-account':
      headerTitle = smallTitle.CLIENT_ACCOUNTS;
      break;
    case 'subcontractor':
      headerTitle = smallTitle.SUBCON_ACCOUNTS;
      break;
    case 'subcontract':
      headerTitle = smallTitle.SUB_CONTRACT_MANAGEMENT;
      break;
    case 'supplier':
      headerTitle = smallTitle.SUPPLIER_ACCOUNTS;
      break;
    case 'client-contract':
      headerTitle = smallTitle.PROJECT_PURCHASES;
      break;
    case 'project-costing':
      headerTitle = smallTitle.PROJECT_COSTING;
      break;
    default:
      headerTitle = smallTitle.PRODUCT;
      break;
  }

  const onSubmit = (data, menu) => {
    if (formMode === 'edit') {
      updateLetterTemplate({
        variables: {
          input: {
            ID: menu?.obj?.ID,
            name: data?.name,
            standardLetterID,
            isDefault: data?.isDefault,
            isWatermark: data?.isWatermark,
            ...(!!data?.isWatermark
              ? { watermarkText: data?.watermarkText }
              : { watermarkText: '' }),
          },
        },
        refetchQueries: [
          { query: GetLetterTemplateDocument, variables: { standardLetterID } },
        ],
      });
    } else if (formMode === 'add') {
      createLetterTemplate({
        variables: {
          input: {
            name: data?.name,
            standardLetterID,
            isDefault: data?.isDefault,
            isWatermark: data?.isWatermark,
            ...(!!data?.isWatermark
              ? { watermarkText: data?.watermarkText }
              : { watermarkText: '' }),
          },
        },
        refetchQueries: [
          { query: GetLetterTemplateDocument, variables: { standardLetterID } },
        ],
      });
    }
  };

  const handleDelete = data => {
    deleteLetterTemplate({
      variables: { input: { ID: data?.ID } },
      refetchQueries: [
        { query: GetLetterTemplateDocument, variables: { standardLetterID } },
      ],
    });
    setDeleteLT(false);
  };

  const checkSameName = () => {
    const getLT = !menu?.obj
      ? getLetterTemplate
      : getLetterTemplate?.filter(x => x?.ID !== menu?.obj?.ID);

    const sameName = getLT?.filter(
      el => el?.name.toLocaleLowerCase() === watch('name')?.toLocaleLowerCase(),
    );

    if (sameName?.length > 0) {
      return true;
    } else return false;
  };

  const hasChanges = () =>
    handleExitConfirmation({
      watch: watch,
      editData: menu?.obj,
      formMode: formMode,
    });

  useEffect(() => {
    if (menu?.obj?.isWatermark) {
      setValue('isWatermark', menu?.obj?.isWatermark);
      setValue('watermarkText', menu?.obj?.watermarkText);
    }
  }, [menu]);

  return (
    <>
      {GetStandardLetterloading && <Loading />}
      {GetLetterTemplateLoading && <Loading />}
      {CreateLetterTemplateLoading && <Loading />}
      {UpdateLetterTemplateLoading && <Loading />}
      {DeleteLetterTemplateLoading && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => {
          history.push({
            pathname: `/${pageMode}/general-settings/standard-letter`,
          });
        }}
        smTitle={headerTitle}
        title={user?.accountName}
        routeSegments={
          globalState?.isPathSysAdmin
            ? [
                { name: 'System Admin' },
                { name: 'System Admin' },
                { name: 'Document Template' },
                { name: 'Letter Template', current: true },
              ]
            : [
                { name: 'System Admin' },
                { name: 'Document Template' },
                { name: 'Letter Template', current: true },
              ]
        }
      />
      <DynamicSubHeader title={getStandardLetter[0]?.name} />
      <ContentWrapper singleDynamicInfo>
        {getLetterTemplate?.length === 0 ? (
          <EmptyList
            title="No Letter Template"
            subtitle="Please create new Letter Template"
          />
        ) : (
          <List className="core-list">
            {getLetterTemplate?.map((v, index) => (
              <ListItem key={v?.ID}>
                <ListItemText
                  primary={
                    <div
                      className="xsTitle"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <span className="">{v?.name}</span>
                      {v?.isDefault === true ? (
                        <Tooltip title="This is the default template">
                          <CheckCircleIcon
                            style={{
                              color: 'limegreen',
                              fontSize: 16,
                              marginLeft: 4,
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </div>
                  }
                  secondary={
                    v?.isWatermark || v?.isAttachedFile ? (
                      <span className="desc">
                        {[
                          v?.isWatermark
                            ? `Watermark: ${v?.watermarkText || 'Yes'}`
                            : null,
                          v?.isAttachedFile ? 'Attached file' : null,
                        ]
                          .filter(Boolean)
                          .join(' | ')}
                      </span>
                    ) : null
                  }
                />
                <ListItemSecondaryAction
                  style={{ top: '50%', transform: 'translateY(-50%)' }}
                >
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    aria-controls="menu-list"
                    aria-haspopup="true"
                    onClick={e => handleClick(e, v?.ID, index, v)}
                  >
                    <MoreVert />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </ContentWrapper>
      <CommonDialog
        fullWidth={true}
        open={open}
        onClose={() => {
          if (hasChanges() === true) {
            setOpenExitConf(true);
          } else {
            setOpen(false);
            reset();
            resetMenu();
          }
        }}
        sections={{
          header: {
            title: 'Letter Template',
            rightText: formMode === 'edit' ? 'Edit' : 'New',
          },
          body: () => (
            <div className="content-container">
              <Controller
                as={TextField}
                id="standard-basic"
                name="name"
                label={'Name'}
                required
                control={control}
                fullWidth
                helperText={
                  checkSameName() === true
                    ? 'Name already exsist.'
                    : errors?.name
                    ? errors?.name.message
                    : null
                }
                error={checkSameName() === true || errors?.name ? true : false}
                ref={register}
              />

              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isDefault')}
                      onChange={e => setValue('isDefault', e.target.checked)}
                      name="isDefault"
                      color="primary"
                      defaultValue={menu?.obj?.isDefault}
                    />
                  }
                  label="Default"
                />
              </FormControl>

              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isWatermark')}
                      onChange={e => {
                        setValue('isWatermark', e.target.checked);
                        setIsWatermark(e.target.checked);
                      }}
                      name="isWatermark"
                      color="primary"
                      // defaultValue={menu?.obj?.isWatermark}
                    />
                  }
                  label="Watermark"
                />
              </FormControl>

              {!!watch('isWatermark') && (
                <Controller
                  as={TextField}
                  id="standard-basic"
                  name="watermarkText"
                  label={'Watermark Name'}
                  required
                  control={control}
                  fullWidth
                  helperText={errors?.watermarkText?.message}
                  error={errors?.watermarkText ? true : false}
                  ref={register}
                />
              )}
            </div>
          ),
          footer: {
            actions: [
              {
                displayText: 'Cancel',
                props: {
                  onClick: () => {
                    if (hasChanges() === true) {
                      setOpenExitConf(true);
                    } else {
                      setOpen(false);
                      reset();
                      resetMenu();
                    }
                  },
                  variant: 'contained',
                  color: 'primary',
                },
              },
              {
                displayText: 'Confirm',
                props: {
                  onClick: () => handleSubmit(data => onSubmit(data, menu))(),
                  variant: 'contained',
                  color: 'primary',
                  disabled: checkSameName(),
                },
              },
            ],
          },
        }}
      />
      <FloatButton
        onClick={() => {
          setValue('name', '');
          setValue('isDefault', false);
          setValue('isWatermark', false);
          setValue('watermarkText', '');
          setOpen(true);
          setFormMode('add');
          resetMenu();
        }}
      />
      <Menu
        id="menu-list"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem
          onClick={() => {
            history.push({
              pathname:
                mode === 'general-settings'
                  ? `/${pageMode}/general-settings/standard-letter/${standardLetterID}/letter-template/${menu?.obj?.ID}/design`
                  : mode === 'company-setup' && !pageMode
                  ? `/company-setup/submenu/${user?.companyID}/standard-letter/${standardLetterID}/letter-template/${menu?.obj?.ID}/design`
                  : `/company-setup/submenu/${user?.companyID}/${pageMode}/standard-letter/${standardLetterID}/letter-template/${menu?.obj?.ID}/design`,
              state: menu?.obj,
            });
          }}
        >
          <span className="">Design</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setValue('name', menu?.obj?.name);

            setValue('isDefault', menu?.obj?.isDefault);
            setOpen(true);
            setFormMode('edit');
          }}
        >
          <span className="">Edit</span>
        </MenuItem>
        <MenuItem
          disabled={menu?.obj?.isDefault}
          onClick={() => setDeleteLT(true)}
        >
          <span className="">Delete</span>
        </MenuItem>
      </Menu>
      <CommonDialog
        fullWidth
        open={deleteLT}
        onClose={() => setDeleteLT(false)}
        sections={{
          header: {
            title: 'Letter Template',
            rightText: `Delete`,
          },
          body: () => (
            <div className="content-wrap full">
              <span
                style={{
                  fontSize: '12px',
                  lineHeight: '14px',
                  fontWeight: 500,
                }}
              >
                {`Are you sure you want to delete ${menu?.obj?.name}?`}
              </span>
            </div>
          ),
          footer: {
            actions: [
              {
                displayText: 'Cancel',
                props: {
                  onClick: () => setDeleteLT(false),
                  variant: 'contained',
                  color: 'primary',
                },
              },
              {
                displayText: 'Confirm',
                props: {
                  onClick: () => {
                    handleDelete(menu?.obj);
                  },
                  variant: 'contained',
                  color: 'primary',
                },
              },
            ],
          },
        }}
      />
      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          setOpen(false);
          setOpenExitConf(false);
          reset();
        }}
      />
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
    </>
  );
};
